import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Button, Input, Spin } from 'antd';
import { SendOutlined, CloseOutlined, MessageOutlined } from '@ant-design/icons';
import n8nChatService from '../../API/services/n8nChatService';
import MessageContent from './MessageContent';
import './N8nChat.scss';
import { t } from '../../i18n/i18n';
import { Plans } from '../../constants/enums';

const N8nChat = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const inactivityTimerRef = useRef(null);
  const user = useSelector((state) => state.auth.user);
  const plan = useSelector((state) => state.auth.plan);

  const INACTIVITY_TIMEOUT = 5 * 60 * 1000; // 5 minutes in milliseconds

  const alkyAvatarUrl = `${process.env.RAZZLE_RUNTIME_ASSETS}/avatar/ALKY%20ROCKET.png`;

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // Generar un ID de sesión único cuando se monta el componente
    const newSessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    setSessionId(newSessionId);
  }, []);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }

    // Prevent body scroll on mobile when chat is open
    if (isOpen && window.innerWidth <= 480) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
    } else {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    }

    // Cleanup on unmount or when chat closes
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    };
  }, [isOpen]);

  // Timer functions for inactivity
  const resetInactivityTimer = () => {
    // Clear existing timer
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }

    // Set new timer for 5 minutes
    inactivityTimerRef.current = setTimeout(() => {
      if (isOpen) {
        // Add closing message before closing chat
        setMessages((prev) => [
          ...prev,
          {
            text: t('N8N_CHAT_CLOSED_MESSAGE_PARTNER_INACTIVITY'),
            sender: 'bot',
          },
        ]);

        // Close chat after a short delay to show the message
        setTimeout(() => {
          handleClose();
        }, 2000);
      }
    }, INACTIVITY_TIMEOUT);
  };

  const clearInactivityTimer = () => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
  };

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      clearInactivityTimer();
    };
  }, []);

  const handleOpen = () => {
    setIsOpen(true);
    // Add welcome message
    setMessages([
      {
        text: user
          ? t('N8N_CHAT_WELCOME_MESSAGE_PARTNER_LOGIN').replace(
              '{firstName}',
              user?.firstName ?? ''
            )
          : t('N8N_CHAT_WELCOME_MESSAGE_PARTNER_LOGOUT'),
        sender: 'bot',
      },
    ]);
    // Start inactivity timer when chat opens
    resetInactivityTimer();
  };

  const handleClose = () => {
    setIsOpen(false);
    setMessages([]);
    setInputMessage('');
    // Clear timer when chat closes
    clearInactivityTimer();
    // Generate new session ID for next chat
    const newSessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    setSessionId(newSessionId);
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const chatInput = inputMessage.trim();
    setInputMessage('');

    // Reset inactivity timer on each message
    resetInactivityTimer();

    // Agregar mensaje del usuario
    setMessages((prev) => [...prev, { text: chatInput, sender: 'user' }]);
    setIsLoading(true);

    // Determine if user is a payment client
    const paymentClient = plan?.tier >= 2 && plan?.planName.toUpperCase() !== Plans.TRIAL;

    try {
      const response = await n8nChatService.sendMessage(chatInput, user, sessionId, paymentClient);

      // Manejar diferentes formatos de respuesta
      let botResponse = '';
      if (typeof response[0] === 'string') {
        botResponse = response[0];
      } else if (response[0].chatInput) {
        botResponse = response[0].chatInput;
      } else if (response[0].response) {
        botResponse = response[0].response;
      } else if (response[0].text) {
        botResponse = response[0].text;
      } else if (response[0].output) {
        botResponse = response[0].output;
      } else if (response[0].message) {
        botResponse = response[0].message;
      } else {
        botResponse = JSON.stringify(data);
      }

      // Agregar respuesta del bot
      setMessages((prev) => [...prev, { text: botResponse, sender: 'bot' }]);
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        {
          text: t('N8N_CHAT_ERROR_MESSAGE'),
          sender: 'bot',
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const toggleChat = () => {
    if (!isOpen) {
      handleOpen();
    } else {
      handleClose();
    }
  };

  return (
    <>
      {/* Botón flotante del chat */}
      {!isOpen && (
        <div className="n8n-chat-button" onClick={toggleChat}>
          <MessageOutlined style={{ fontSize: '24px', color: 'white' }} />
        </div>
      )}

      {/* Ventana del chat */}
      {isOpen && (
        <div className="n8n-chat-window">
          <div className="n8n-chat-header">
            <div className="n8n-header-title">
              <MessageOutlined style={{ marginRight: '8px' }} />
              <span>Asistente Virtual</span>
            </div>
            <Button
              type="text"
              icon={<CloseOutlined />}
              onClick={toggleChat}
              className="n8n-close-button"
            />
          </div>

          <div className="n8n-chat-messages">
            {messages.map((msg, index) => (
              <div
                key={index}
                className={`n8n-message ${
                  msg.sender === 'user' ? 'n8n-user-message' : 'n8n-bot-message'
                }`}
              >
                {msg.sender === 'bot' && (
                  <img src={alkyAvatarUrl} alt="Alky Avatar" className="n8n-bot-avatar" />
                )}
                <div className="n8n-message-content">
                  <MessageContent text={msg.text} />
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="n8n-message n8n-bot-message">
                <img src={alkyAvatarUrl} alt="Alky Avatar" className="n8n-bot-avatar" />
                <div className="n8n-message-content">
                  <Spin size="small" />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="n8n-chat-input">
            <Input.TextArea
              ref={inputRef}
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={t('N8N_CHAT_PLACEHOLDER_MESSAGE_PARTNER')}
              autoSize={{ minRows: 1, maxRows: 4 }}
              disabled={isLoading}
              className="n8n-input-field"
            />
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={handleSendMessage}
              disabled={!inputMessage.trim() || isLoading}
              className="n8n-send-button"
            />
          </div>
        </div>
      )}
    </>
  );
};

export default N8nChat;
